import type { ActionPhaseName, SpinResponse } from "@slot/client-engine";
import { fx, spinFixture } from "@slot/game-shell";
import { cannonFire, cannonLoadout } from "../cannonFixtureHelpers";

const BET = "100";
const FEATURE = "super_bonus_buy";
const AWARDED = 3;

const mask = (indices: number[]): string => Array.from({ length: 20 }, (_, i) => (indices.includes(i) ? "1" : "0")).join("");

/** The DSL's event builder, as `spinFixture(...).event` hands it over. */
type FixtureEvent = Parameters<Parameters<ReturnType<typeof spinFixture>["event"]>[2]>[0];

/** An action from the shell DSL or the cannon helpers, authored on the event with its phase. */
function act(e: FixtureEvent, a: { type: string; phase: ActionPhaseName; payload: object }): void {
  e.action(a.type, a.payload as Record<string, unknown>, { actionPhaseName: a.phase });
}

/** A pay-anywhere win: its cells, the paytable's base amount, the fired rung, and the paid amount (base × rung). */
function win(e: FixtureEvent, symbol: string, cells: number[], base: string, rung: string, amount: string): void {
  const m = mask(cells);
  const count = String(cells.length);
  act(e, fx.gridWinHighlight({ positionMask: m, winAmount: amount, symbolIdentifier: symbol, symbolCount: count, lines: [{ positionMask: m, winAmount: amount, symbolCount: count, baseWinAmount: base, multiplierApplied: rung }] }));
}

/** Every event of a free spin carries the same feature snapshot: the spins left after it, the session total after it. */
function freeSpin(e: FixtureEvent, remaining: number, sessionTotal: string): FixtureEvent {
  return e
    .reelSet("free_spins")
    .betAmount(BET)
    .featureStateSet({ featureIdentifier: FEATURE, featureCountRemaining: String(remaining), featureAccumulatedWinMinor: sessionTotal, featurePhase: "update" });
}

/**
 * BONUS BUY (`super_bonus_buy`): a free-spins session in the backend's shape (slot-backend-platform
 * `game-math-cannon`, `evaluateSpin`). Every free spin runs the base game's tumble loop: a pay-anywhere win (6+ of a
 * symbol, paid bet × paytable × the fired rung) is highlighted, the cannon fires, the winners are removed and the grid
 * tumbles, until a dead grid ends the spin. So no spin ends with a win on the grid. The cannon climbs across the
 * whole session: each free spin's reveal reloads it where the session is up to.
 *
 *   trigger reveal: no win, 3 FS (one per reel), the cannon loaded at x2, the feature won
 *   fs:1  H2 ×6: 90 × 2 = 180; tumble; L5 ×6: 40 × 3 = 120; tumble; dead            spin 300
 *   fs:2  dead reveal; the cannon reloaded at x4                                     spin 0
 *   fs:3  H3 ×6: 125 × 4 = 500; tumble; dead                                         spin 500
 *   feature exit: the session total 800
 *
 * The backend awards 10 spins; three keep the e2e rounds short. Every collapse grid is the exact gravity of the grid
 * before it minus the removed cells: survivors keep their symbol and fall, fresh symbols fill only the vacated top
 * cells. No W, CH or FS lands in a free spin (a wild would join a count, a chance symbol would detonate on the dead
 * grid). Each spin stays under the nice tier (8 × bet), as the backend would otherwise add `present_big_win`. Until
 * 2026-10-01 this round was a payline-template leftover: three 3-symbol line wins with no removal, tumble or cannon,
 * so the last win stayed on the grid behind and after the feature-finished panel.
 */
export function fsFixtureFreeSpins(): SpinResponse {
  return spinFixture("fs-free-spins")
    .balance("EUR", "99900")

    // --- seq 0: the trigger reveal — no win, three scatters, the cannon loaded, the feature won ---
    .event("reveal", 0, (e) => {
      e.reelSet("default").betAmount(BET).accumulatedWinAfter("0").gridRows([
        ["L2", "L4", "L5", "L1", "L3"],
        ["L3", "L1", "L4", "L2", "L5"],
        ["FS", "L4", "FS", "L2", "FS"],
        ["L1", "L5", "L2", "L4", "L3"],
      ]);
      act(e, fx.spinBoundaryStart("base:1", "base"));
      act(e, cannonLoadout(0));
      act(e, fx.presentScatterState({ positionMask: mask([10, 12, 14]), scatterCount: "3", scatterSymbolIdentifier: "FS" }));
      act(e, fx.bonusFeatureWon(FEATURE, String(AWARDED)));
      act(e, fx.updateTotalWinMeter("0"));
      act(e, fx.spinBoundaryEnd("base:1", "0"));
    })

    // --- seq 1: feature enter ---
    .event("feature_enter", 1, (e) => {
      e.betAmount(BET).featureStateSet({ featureIdentifier: FEATURE, featureCountRemaining: String(AWARDED), featureAccumulatedWinMinor: "0", featurePhase: "enter" });
      act(e, fx.updateTotalWinMeter("0"));
    })

    // --- fs:1, seq 2-4: H2 ×6 fires x2, tumbles into L5 ×6, which fires x3; the second tumble is dead ---
    .event("fs_reveal", 2, (e) => {
      freeSpin(e, 2, "300").accumulatedWinAfter("180").gridRows([
        ["L4", "L1", "L5", "L2", "L3"],
        ["H2", "H2", "H2", "L1", "L5"],
        ["H2", "H2", "L4", "L2", "L5"],
        ["H2", "L3", "L4", "L5", "L1"],
      ]);
      act(e, fx.spinBoundaryStart("fs:1", "free_spins"));
      act(e, cannonLoadout(0));
      win(e, "H2", [5, 6, 7, 10, 11, 15], "90", "2", "180");
      act(e, cannonFire(0));
      act(e, fx.removeSymbolsByMask(mask([5, 6, 7, 10, 11, 15])));
      act(e, fx.updateTotalWinMeter("180"));
    })
    .event("fs_collapse", 3, (e) => {
      freeSpin(e, 2, "300").accumulatedWinAfter("300").gridRows([
        ["L5", "L5", "L2", "L2", "L3"],
        ["H1", "H4", "L5", "L1", "L5"],
        ["H3", "L1", "L4", "L2", "L5"],
        ["L4", "L3", "L4", "L5", "L1"],
      ]);
      win(e, "L5", [0, 1, 7, 9, 14, 18], "40", "3", "120");
      act(e, cannonFire(1));
      act(e, fx.removeSymbolsByMask(mask([0, 1, 7, 9, 14, 18])));
      act(e, fx.updateTotalWinMeter("300"));
    })
    .event("fs_collapse", 4, (e) => {
      freeSpin(e, 2, "300").accumulatedWinAfter("300").gridRows([
        ["H2", "L5", "H1", "H3", "L4"],
        ["H1", "H4", "L2", "L2", "H2"],
        ["H3", "L1", "L4", "L1", "L3"],
        ["L4", "L3", "L4", "L2", "L1"],
      ]);
      act(e, fx.spinBoundaryEnd("fs:1", "300"));
    })

    // --- fs:2, seq 5: a dead reveal; the cannon reloads where the session is up to (x4) ---
    .event("fs_reveal", 5, (e) => {
      freeSpin(e, 1, "300").accumulatedWinAfter("0").gridRows([
        ["L3", "H4", "L1", "L5", "L2"],
        ["L2", "L4", "H1", "L3", "L5"],
        ["H3", "L1", "L5", "L4", "H2"],
        ["L4", "L2", "L3", "L1", "L5"],
      ]);
      act(e, fx.spinBoundaryStart("fs:2", "free_spins"));
      act(e, cannonLoadout(2));
      act(e, fx.spinBoundaryEnd("fs:2", "0"));
    })

    // --- fs:3, seq 6-7: H3 ×6 fires x4; the tumble is dead ---
    .event("fs_reveal", 6, (e) => {
      freeSpin(e, 0, "800").accumulatedWinAfter("500").gridRows([
        ["L2", "L4", "L1", "L5", "L3"],
        ["L5", "H3", "H3", "H3", "L2"],
        ["L1", "H3", "H3", "L4", "L5"],
        ["L4", "L2", "H3", "L1", "L3"],
      ]);
      act(e, fx.spinBoundaryStart("fs:3", "free_spins"));
      act(e, cannonLoadout(2));
      win(e, "H3", [6, 7, 8, 11, 12, 17], "125", "4", "500");
      act(e, cannonFire(2));
      act(e, fx.removeSymbolsByMask(mask([6, 7, 8, 11, 12, 17])));
      act(e, fx.updateTotalWinMeter("500"));
    })
    .event("fs_collapse", 7, (e) => {
      freeSpin(e, 0, "800").accumulatedWinAfter("500").gridRows([
        ["L2", "H1", "H4", "L3", "L3"],
        ["L5", "H2", "H1", "L5", "L2"],
        ["L1", "L4", "H2", "L4", "L5"],
        ["L4", "L2", "L1", "L1", "L3"],
      ]);
      act(e, fx.spinBoundaryEnd("fs:3", "500"));
    })

    // --- seq 8: feature exit — no actions; the round total rides the event fields ---
    .event("feature_exit", 8, (e) => {
      e.betAmount(BET).accumulatedWinAfter("800").featureStateSet({ featureIdentifier: FEATURE, featureCountRemaining: "0", featureAccumulatedWinMinor: "800", featurePhase: "exit" });
    })
    .build();
}
